// This file is part of Shadcn Admin Kit (https://github.com/marmelab/shadcn-admin-kit)
import { AuthCallback } from "@/components/admin/authentication"
import { Layout } from "@/components/admin/layout"
import { LoginPage } from "@/components/admin/login-page"
import { Ready } from "@/components/admin/ready"
import { ThemeProvider } from "@/components/admin/theme-provider"
import { i18nProvider as defaultI18nProvider } from "@/lib/i18nProvider"
import {
  CoreAdminContext,
  type CoreAdminContextProps,
  type CoreAdminProps,
  CoreAdminUI,
  type CoreAdminUIProps,
  localStorageStore,
} from "ra-core"
import { useEffect } from "react"
import { BrowserRouter } from "react-router-dom"

const defaultStore = localStorageStore()

const AdminContext = (props: CoreAdminContextProps) => (
  <CoreAdminContext {...props} />
)

const AdminUI = (props: CoreAdminUIProps) => {
  const { disableTelemetry = false, ...rest } = props

  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return
    }
    const img = new Image()
    img.src = `https://shadcn-admin-kit-telemetry.marmelab.com/shadcn-admin-kit-telemetry?domain=${window.location.hostname}`
  }, [disableTelemetry])

  return (
    <ThemeProvider>
      <CoreAdminUI
        layout={Layout}
        loginPage={LoginPage}
        ready={Ready}
        authCallbackPage={AuthCallback}
        disableTelemetry // Disable telemetry in CoreAdminUI to avoid double logging
        {...rest}
      />
    </ThemeProvider>
  )
}

export const Admin = (props: CoreAdminProps) => {
  const {
    accessDenied,
    authCallbackPage = AuthCallback,
    authenticationError,
    authProvider,
    basename,
    catchAll,
    children,
    dashboard,
    dataProvider,
    disableTelemetry,
    error,
    i18nProvider = defaultI18nProvider,
    layout = Layout,
    loading,
    loginPage = LoginPage,
    queryClient,
    ready = Ready,
    requireAuth,
    store = defaultStore,
    title = "Shadcn Admin",
  } = props
  return (
    <BrowserRouter basename={basename}>
      <AdminContext
        authProvider={authProvider}
        dataProvider={dataProvider}
        i18nProvider={i18nProvider}
        queryClient={queryClient}
        store={store}
      >
        <AdminUI
          accessDenied={accessDenied}
          authCallbackPage={authCallbackPage}
          authenticationError={authenticationError}
          catchAll={catchAll}
          dashboard={dashboard}
          disableTelemetry={disableTelemetry}
          error={error}
          layout={layout}
          loading={loading}
          loginPage={loginPage}
          ready={ready}
          requireAuth={requireAuth}
          title={title}
        >
          {children}
        </AdminUI>
      </AdminContext>
    </BrowserRouter>
  )
}
